import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { DataService } from "src/services/data-service";

interface AddImagePopupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddImagePopup: React.FC<AddImagePopupProps> = ({ open, onClose, onSuccess }) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(new Date().toISOString());
      setName("");
      setFile(null);
    }
  }, [open]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      const maxSize = 5 * 1024 * 1024;
  
      if (selectedFile.size > maxSize) {
        toast.error("File size exceeds the maximum limit of 5 MB.");
        return;
      }
  
      setFile(selectedFile);
    }
  };

  const handleSubmit = async () => {
    if (!name || !file) {
      toast.error("All fields are required!");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      const data = { Code: code, Name: name };

      formData.append("request", JSON.stringify(data));
      formData.append("formFile", file, file.name);

      const req = await DataService.postForm("api/media/create", formData);

      if (req.ok) {
        toast.success("Image added successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error("An error occurred while uploading the image.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ "& .MuiDialog-paper": { height: "400px" } }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
        Add New Image
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          height: "300px",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              margin="dense"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", marginBottom: "5px" }}
            >
              Upload Image:
            </Typography>
            <Button
              variant="contained"
              component="label"
              fullWidth
              color="primary"
            >
              Choose File
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                hidden
              />
            </Button>
            {file && (
              <Typography
                variant="body2"
                sx={{
                  marginTop: "5px",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Selected: {file.name}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", padding: "10px 20px" }}>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddImagePopup;
